import React from 'react'
import { Box, HStack, VStack, Text } from '@chakra-ui/react'
import cx from 'classnames'
import Select, { Props, components as SelectComponents } from 'react-select'
import { IOption, IOptionWithIcon } from 'types/common'
import IconBuilder from 'pages/IconBuilderPage/components/IconBuilder'
import { DARK_PAGE_BG, DARK_TABLE_BORDER, DARK_TEXT_MUTED } from 'constants/dark-tokens'
import styles from './dropdownInput.module.scss'

export interface IDropdownInputProps<T> extends Omit<Props, 'options'> {
  label?: string
  options: IOption<T>[] | IOptionWithIcon<T>[]
  defaultValue?: IOption<T>
  value?: IOption<T> | IOption<T>[]
  isRequired?: boolean
  hasNoSeparator?: boolean
  indicatorsContainer?: React.ReactNode
  className?: string
  containerClassName?: string
  labelClassName?: string
  autoComplete?: string
  isSearchable?: boolean
  hasIcon?: boolean
  /** Opt-in dark-surface styling (default false; light path byte-for-byte unchanged). */
  dark?: boolean
}

const DropdownInput = <T,>(props: IDropdownInputProps<T>) => {
  const {
    label,
    isRequired = false,
    hasNoSeparator,
    indicatorsContainer,
    className,
    containerClassName,
    labelClassName,
    isSearchable = true,
    hasIcon = false,
    dark = false
  } = props
  const components: Record<string, unknown> = {}

  if (hasNoSeparator) {
    components.IndicatorSeparator = null
  }

  if (indicatorsContainer) {
    components.IndicatorsContainer = () => indicatorsContainer
  }

  if (hasIcon || dark) {
    components.Option = (optionProps: any) => {
      // react-select's base Option paints its own emotion styles (default focused
      // bg #DEEBFF) that the styles prop can't fully override on the dark surface,
      // so we render the option ourselves via innerRef/innerProps (preserves click,
      // focus, aria, scroll-into-view) with the standard faint wash instead.
      if (dark) {
        const { innerRef, innerProps, isFocused, isSelected } = optionProps
        const bg = isSelected
          ? 'rgba(255, 255, 255, 0.12)'
          : isFocused
            ? 'rgba(255, 255, 255, 0.08)'
            : 'transparent'
        return (
          <Box
            ref={innerRef}
            {...innerProps}
            px={3}
            py="10px"
            cursor="pointer"
            color="white"
            background={bg}
          >
            <HStack spacing={2} width="full">
              {hasIcon && optionProps?.data?.icon && (
                <Box flexShrink={0}>
                  <IconBuilder icon={optionProps.data.icon} isActive size={32} />
                </Box>
              )}
              <Text>{optionProps?.data?.label ?? ''}</Text>
            </HStack>
          </Box>
        )
      }
      return (
        <SelectComponents.Option {...optionProps}>
          <HStack spacing={2} width="full">
            {hasIcon && <IconBuilder icon={optionProps?.data?.icon} isActive size={32} />}
            <Text>{optionProps?.data?.label ?? ''}</Text>
          </HStack>
        </SelectComponents.Option>
      )
    }
  }

  const darkStyles = dark
    ? {
        control: (provided: Record<string, unknown>) => ({
          ...provided,
          background: 'rgba(255, 255, 255, 0.06)',
          borderColor: DARK_TABLE_BORDER,
          color: 'white',
          '&:hover': { borderColor: DARK_TABLE_BORDER },
          boxShadow: 'none'
        }),
        input: (provided: Record<string, unknown>) => ({
          ...provided,
          color: 'white'
        }),
        placeholder: (provided: Record<string, unknown>) => ({
          ...provided,
          color: 'rgba(255, 255, 255, 0.5)'
        }),
        singleValue: (provided: Record<string, unknown>) => ({
          ...provided,
          color: 'white'
        }),
        // Selected chips (isMulti) — dark pill instead of the default light-grey card.
        multiValue: (provided: Record<string, unknown>) => ({
          ...provided,
          background: 'rgba(255, 255, 255, 0.12)',
          borderRadius: '4px'
        }),
        multiValueLabel: (provided: Record<string, unknown>) => ({
          ...provided,
          color: 'white'
        }),
        multiValueRemove: (provided: Record<string, unknown>) => ({
          ...provided,
          color: 'rgba(255, 255, 255, 0.6)',
          ':hover': { background: 'rgba(255, 255, 255, 0.16)', color: 'white' }
        }),
        // No border on the open menu (it read as a white outline on the dark surface);
        // the drop shadow alone separates it from the page.
        menu: (provided: Record<string, unknown>) => ({
          ...provided,
          background: DARK_PAGE_BG,
          border: 'none',
          borderRadius: 8,
          boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.4)',
          overflow: 'hidden',
          zIndex: 9999
        }),
        menuList: (provided: Record<string, unknown>) => ({
          ...provided,
          background: DARK_PAGE_BG,
          // The menu wrapper paints the border/radius; keep the list flush so no
          // light hairline shows between the list and the rounded menu edge.
          border: 'none',
          zIndex: 9999
        }),
        menuPortal: (provided: Record<string, unknown>) => ({
          ...provided,
          zIndex: 9999
        })
      }
    : {
        menu: (provided: Record<string, unknown>) => ({ ...provided, zIndex: 9999 }),
        menuPortal: (provided: Record<string, unknown>) => ({ ...provided, zIndex: 9999 })
      }

  return (
    <VStack className={cx(containerClassName, styles.container)} marginTop={!label ? '0 !important' : 2}>
      <div className="w-100">
        {label && (
          <span
            className={cx(styles.fieldLabel, labelClassName)}
            style={dark ? { color: DARK_TEXT_MUTED } : undefined}
          >
            {label}
          </span>
        )}
        {isRequired && <span className={styles.required}> *</span>}
        <Select
          {...props}
          isSearchable={isSearchable}
          className={cx(styles.dropdown, className)}
          components={components}
          menuPlacement="bottom"
          // @ts-ignore
          autoComplete="off"
          styles={darkStyles}
        />
      </div>
    </VStack>
  )
}

export default DropdownInput
